import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Upload, Download, Trash2, File } from 'lucide-react'
import { useFileManagementStore } from '../stores/fileManagementStore'
import { useAuthStore } from '../stores/authStore'

export default function SimpleFileManagePage() {
  const navigate = useNavigate()
  const { isAdmin } = useAuthStore()
  const {
    files,
    loading,
    error,
    fetchFiles,
    uploadFile,
    deleteFile,
    downloadFile,
    clearError
  } = useFileManagementStore()

  const [uploading, setUploading] = useState(false)
  const [uploadSuccess, setUploadSuccess] = useState('')

  useEffect(() => {
    fetchFiles()
  }, [fetchFiles])

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Allowed file types with proper validation
    const allowedTypes = [
      'application/pdf',                    // PDF
      'application/msword',                 // Word .doc  
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // Word .docx
      'application/vnd.ms-excel',           // Excel .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // Excel .xlsx
      'image/png',                          // PNG images
      'image/jpeg',                         // JPEG images
      'image/jpg',                          // JPG images (some browsers)
      'text/plain'                          // Text files
    ]

    const allowedExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.png', '.jpg', '.jpeg', '.txt']
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()

    // Check file type (both MIME type and extension)
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      alert('Dateityp nicht erlaubt. Nur PDF, Word, Excel, Bilder (PNG/JPG) und Textdateien sind zugelassen.')
      e.target.value = '' // Reset input
      return
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Datei ist zu groß. Maximum 10MB erlaubt.')
      e.target.value = '' // Reset input
      return
    }

    setUploading(true)
    setUploadSuccess('')
    clearError()

    try {
      const result = await uploadFile(file, 'Dokumente', '')
      
      if (result.error) {
        alert(`Fehler beim Hochladen: ${result.error}`)
      } else {
        setUploadSuccess(`✅ "${file.name}" erfolgreich hochgeladen!`)
        // Reset the input
        e.target.value = ''
        // Clear success message after 4 seconds
        setTimeout(() => setUploadSuccess(''), 4000)
      }
    } catch (error) {
      alert('Unerwarteter Fehler beim Hochladen')
    } finally {
      setUploading(false)
    }
  }

  const handleDownload = async (fileId: string) => {
    const result = await downloadFile(fileId)
    if (result.error) {
      alert(`Fehler beim Herunterladen: ${result.error}`)
    }
  }

  const handleDelete = async (fileId: string, fileName: string) => {
    if (!confirm(`Datei "${fileName}" wirklich löschen?`)) return

    const result = await deleteFile(fileId)
    if (result.error) {
      alert(`Fehler beim Löschen: ${result.error}`)
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getFileIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return '📄'
    if (fileType.includes('word') || fileType.includes('document')) return '📝'
    if (fileType.includes('excel') || fileType.includes('spreadsheet') || fileType.includes('sheet')) return '📊'
    if (fileType.includes('image') || fileType.includes('png') || fileType.includes('jpg') || fileType.includes('jpeg')) return '🖼️'
    if (fileType.includes('text') || fileType.includes('plain')) return '�'
    return '�📁'
  }

  const getFileIconColor = (fileType: string) => {
    if (fileType.includes('pdf')) return 'text-red-500'
    if (fileType.includes('word') || fileType.includes('document')) return 'text-blue-500'
    if (fileType.includes('excel') || fileType.includes('spreadsheet') || fileType.includes('sheet')) return 'text-green-500'
    if (fileType.includes('image')) return 'text-purple-500'
    if (fileType.includes('text')) return 'text-gray-500'
    return 'text-gray-400'
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/admin')}
          className="btn btn-outline"
        >
          <ArrowLeft size={16} />
          Zurück
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dateiablage</h1>
          <p className="text-gray-600">Einfache Dateiverwaltung für FC Frick</p>
        </div>
      </div>

      {/* Success Message - Enhanced */}
      {uploadSuccess && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-300 text-green-700 px-6 py-4 rounded-xl mb-6 shadow-sm animate-pulse">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🎉</span>
            <span className="font-semibold">{uploadSuccess}</span>
          </div>
        </div>
      )}

      {/* Error Message - Enhanced */}
      {error && (
        <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-300 text-red-700 px-6 py-4 rounded-xl mb-6 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <span className="text-2xl">⚠️</span>
            <span className="font-semibold">{error}</span>
          </div>
          <button onClick={clearError} className="text-red-600 hover:text-red-800 text-xl font-bold">
            ✕
          </button>
        </div>
      )}

      {/* Upload Section - Enhanced Design */}
      {isAdmin() && (
        <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl border border-blue-200 p-6 mb-6 shadow-sm">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">📁 Datei hochladen</h2>
          
          <div className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 ${
            uploading 
              ? 'border-blue-400 bg-blue-50 scale-105' 
              : 'border-gray-300 hover:border-blue-400 hover:bg-blue-25'
          }`}>
            {uploading ? (
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mb-4"></div>
                <p className="text-blue-700 font-semibold text-lg">Datei wird hochgeladen...</p>
                <p className="text-blue-600 mt-2">Bitte warten Sie einen Moment</p>
                <div className="w-64 bg-blue-200 rounded-full h-2 mt-4">
                  <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '70%' }}></div>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <File className="w-16 h-16 mx-auto mb-2 text-blue-400" />
                  <div className="text-4xl mb-2">📎</div>
                </div>
                <label className="cursor-pointer group">
                  <span className="inline-flex items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 group-hover:scale-105 shadow-lg">
                    <Upload size={20} />
                    Datei auswählen
                  </span>
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={uploading}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt"
                    multiple={false}
                  />
                </label>
                <div className="mt-4 space-y-2">
                  <p className="text-gray-600 font-medium">
                    📄 PDF • 📝 Word • 📊 Excel • 🖼️ Bilder • 📄 Text
                  </p>
                  <p className="text-sm text-gray-500">
                    Maximum 10MB pro Datei
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Files List - Enhanced Design */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">📋</span>
              <h2 className="text-xl font-semibold text-gray-800">Verfügbare Dateien</h2>
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                {files.length} {files.length === 1 ? 'Datei' : 'Dateien'}
              </span>
            </div>
            {files.length > 0 && (
              <div className="text-sm text-gray-500">
                Gesamt: {files.reduce((total, file) => total + file.fileSize, 0) > 1024 * 1024 
                  ? (files.reduce((total, file) => total + file.fileSize, 0) / (1024 * 1024)).toFixed(1) + ' MB'
                  : (files.reduce((total, file) => total + file.fileSize, 0) / 1024).toFixed(1) + ' KB'
                }
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Dateien werden geladen...</p>
          </div>
        ) : files.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <div className="text-6xl mb-4">📂</div>
            <p className="text-lg font-medium mb-2">Noch keine Dateien hochgeladen</p>
            <p className="text-sm">Laden Sie Ihre erste Datei hoch, um zu beginnen</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {files.map((file, index) => (
              <div key={file.id} className={`p-4 hover:bg-gray-50 transition-colors duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="relative">
                      <span className="text-3xl">{getFileIcon(file.fileType)}</span>
                      <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full ${getFileIconColor(file.fileType)} opacity-80`}></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate text-lg">
                        {file.originalName || file.name}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                        <span className="flex items-center gap-1">
                          📊 {formatFileSize(file.fileSize)}
                        </span>
                        <span className="flex items-center gap-1">
                          📅 {new Date(file.uploadedAt).toLocaleDateString('de-CH', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleDownload(file.id)}
                      className="flex items-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 px-4 py-2 rounded-lg transition-colors duration-200 font-medium"
                      title="Herunterladen"
                    >
                      <Download size={16} />
                      <span className="hidden sm:inline">Download</span>
                    </button>
                    
                    {isAdmin() && (
                      <button
                        onClick={() => handleDelete(file.id, file.originalName || file.name)}
                        className="flex items-center gap-2 bg-red-50 hover:bg-red-100 text-red-700 px-4 py-2 rounded-lg transition-colors duration-200 font-medium"
                        title="Löschen"
                      >
                        <Trash2 size={16} />
                        <span className="hidden sm:inline">Löschen</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
